import React from 'react';
import AnalyzeItem from './analyze-item';
import {ColorGetLatestContentResponse, getLatest} from '../../color-service';
import NoData from '@/components/basic/error-pages/no-data';
import {useTranslation} from 'react-i18next';
import ShowMoreButton from '@/common-pages/result/components/show-more-button';

const pageSize = 10;

const Analyze = ({
  configId,
  isActive = false,
  refreshCode = 0,
  onLoadingChange,
}: {
  configId?: number;
  refreshCode?: number;
  isActive?: boolean;
  onLoadingChange?: (loading: boolean) => void;
}) => {
  const {t} = useTranslation();
  const [total, setTotal] = React.useState(0);
  const [list, setList] = React.useState<ColorGetLatestContentResponse[]>([]);
  const [firstLoad, setFirstLoad] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [pageNo, setPageNo] = React.useState(1);

  const getList = async (
    id: number,
    page: number = 1,
    append: boolean = false,
  ) => {
    try {
      setLoading(true);
      const res = await getLatest({
        configId: id,
        pageNo: page,
        pageSize,
      });
      if (res) {
        setTotal(res.totalSize);
        setPageNo(page);
        setList(prev => (append ? [...prev, ...res.content] : res.content));
      }
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
  };

  React.useEffect(() => {
    if (configId && isActive) {
      getList(configId).then();
    }
  }, [configId, isActive]);

  React.useEffect(() => {
    if (refreshCode && isActive && configId) {
      getList(configId).then();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCode]);

  React.useEffect(() => {
    onLoadingChange?.(firstLoad || loading);
  }, [firstLoad, loading, onLoadingChange]);

  return (
    <>
      {!firstLoad && !loading && list && list.length === 0 ? <NoData /> : null}
      {list.length > 0 ? (
        <div className="relative z-0 rounded-lg mx-3 mb-3">
          <div className="relative z-0 flex flex-col rounded-lg overflow-hidden result-table w-full">
            <div className="bg-table-header flex flex-row py-2 text-xs text-sec result-table-th">
              <div className="flex flex-row flex-1 items-center mx-3">
                <div className="flex-1 text-left">
                  {t('game-page.label.number')}
                </div>
                <div className="flex-[2] text-right">
                  {t('game-page.label.perriod')}
                </div>
              </div>
            </div>
            {list.map((item, index) => (
              <AnalyzeItem
                key={item.id || index}
                issueNum={item.issueNo}
                digit={item.digitResult}
                color={item.colorResult}
                nextDigit={list[index + 1]?.digitResult}
                index={index}
              />
            ))}
            {pageNo * pageSize < total ? (
              <div className="flex p-3 bg-white">
                <ShowMoreButton
                  label={t('home.label.showMore')}
                  onPress={() => {
                    if (configId && !loading) {
                      getList(configId, pageNo + 1, true).then();
                    }
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default React.memo(Analyze);
